/**
 * A merch order's delivery, as the purchase describes it (LIS-1173, the
 * purchase design's §2.3 and §2.7), for the sheet's basket: none for
 * tickets; the listing's own postage ladder, one option, at once; or the
 * store's live options, asked about one destination (`quoted`). Postage
 * not yet measured is never a fee, and a demo is never asked about. Each
 * answer says which destination it was asked about and whether that was the
 * buyer's own or the country's stand-in (`preliminary`): a start takes only
 * the buyer's.
 */
import { merchDeliveryLabel, vendorDeliveryFeeMinor } from "@libs/platform-wire";
import type { Destination, Purchase } from "@libs/platform-wire/purchase";
import type { DeliveryEstimate, DeliveryOption } from "../contracts";
import {
  DELIVERY_POLL_MS,
  DELIVERY_SETTLE_MS,
  DELIVERY_WAIT_MS,
  LISTING_DELIVERY_CODE,
  deliveryKeyOf,
  type DeliveryLines,
} from "../delivery";
import { addressFieldOf, destinationKind } from "./form";
import type { PurchaseTransport } from "./transport";

/** The delivery for the basket. `ready` names the destination its options
 *  were asked about and its kind (null for the listing's own ladder, which
 *  needs no estimate). */
export type PurchaseDeliveryQuote =
  | { status: "idle"; key: null }
  | { status: "loading"; key: string }
  | {
      status: "ready";
      key: string;
      estimateId: string | null;
      options: DeliveryOption[];
      destination: Destination | null;
      kind: "buyer" | "preliminary" | null;
    }
  | { status: "short"; key: string; stock: DeliveryEstimate["stock"] }
  | { status: "undeliverable"; key: string }
  | { status: "failed"; key: string; message: string | null };

const pause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** The basket's subtotal at the purchase's prices, booking fees included. */
const subtotalOf = (purchase: Purchase, lines: DeliveryLines) =>
  lines.reduce((sum, line) => {
    const item = purchase.picker.items.find((candidate) => candidate.id === line.item_id);
    return item ? sum + line.quantity * (item.priceMinor + item.feeMinor) : sum;
  }, 0);

/** Where a quoted basket is asked about before the buyer names a place:
 *  the country the purchase was read for. */
const standIn = (purchase: Purchase): Destination | null =>
  purchase.delivery.kind === "quoted" ? { country: purchase.delivery.country, postcode: null, region: null } : null;

/**
 * What the store is asked about for a destination: the destination as it
 * reads for the purchase's address; where its postcode or region doesn't
 * (a postcode typed for another country), its country's stand-in, which a
 * start never takes; nothing for a country the address refuses or doesn't
 * list. The form says what is wrong with what was typed.
 */
function askable(purchase: Purchase, where: Destination | null): Destination | null {
  const address = addressFieldOf(purchase.form.sections);
  if (!where || !address || destinationKind(where, address) !== null) return where;
  const country = { country: where.country, postcode: null, region: null };
  return destinationKind(country, address) === null ? null : country;
}

export function createPurchaseDelivery(input: {
  accessId: string;
  /** The purchase sheet's own id: the server asks the store for the sheet's
   *  basket under it. */
  sheetId: string;
  transport: Pick<PurchaseTransport, "estimateDelivery" | "deliveryEstimate"> | null;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}) {
  const { sheetId, accessId } = input;
  const sleep = input.sleep ?? pause;
  const now = input.now ?? Date.now;
  let purchase: Purchase | null = null;
  let destination: Destination | null = null;
  let lines: DeliveryLines = [];
  let state: PurchaseDeliveryQuote = { status: "idle", key: null };
  /** What the current answer was asked for: the listing, its delivery, the
   *  destination and the basket. */
  let askedFor: string | null = null;
  let generation = 0;
  let disposed = false;
  const listeners = new Set<() => void>();
  const set = (next: PurchaseDeliveryQuote) => {
    if (disposed) return;
    state = next;
    for (const listener of listeners) listener();
  };
  const failed = (key: string, current: number) => {
    if (current === generation) set({ status: "failed", key, message: null });
  };

  const ask = async (key: string, read: Purchase, where: Destination, current: number) => {
    const live = () => !disposed && current === generation;
    const transport = input.transport;
    if (!transport) {
      set({ status: "failed", key, message: null });
      return;
    }
    const address = addressFieldOf(read.form.sections);
    const kind = address ? destinationKind(where, address) : null;
    const until = now() + DELIVERY_WAIT_MS;
    let estimate: DeliveryEstimate | null = null;
    let failures = 0;
    while (live() && now() < until) {
      try {
        estimate = estimate
          ? await transport.deliveryEstimate(sheetId, estimate.id)
          : await transport.estimateDelivery({
              accessId,
              sheetId,
              listingId: read.listingId,
              lines: JSON.parse(key) as { item_id: string; quantity: number }[],
              destination: where,
            });
        failures = 0;
      } catch (error) {
        const status = (error as { status?: unknown } | null)?.status;
        // Refused for good (a basket the listing cannot sell, an estimate
        // gone): asked anew once, then said.
        if (typeof status === "number" && status >= 400 && status < 500 && status !== 429) {
          if (estimate && failures === 0) {
            estimate = null;
            failures++;
            continue;
          }
          if (live()) set({ status: "failed", key, message: null });
          return;
        }
        if (++failures > 3) break;
        await sleep(Math.min(4_000, 500 * 2 ** failures));
        continue;
      }
      if (!live()) return;
      if (estimate.state !== "pending") break;
      await sleep(DELIVERY_POLL_MS);
    }
    if (!live()) return;
    if (!estimate) {
      set({ status: "failed", key, message: null });
      return;
    }
    switch (estimate.state) {
      case "pending":
        set({ status: "failed", key, message: null });
        return;
      case "ready":
        set(
          estimate.options.length
            ? { status: "ready", key, estimateId: estimate.id, options: estimate.options, destination: where, kind }
            : { status: "undeliverable", key },
        );
        return;
      case "short":
        set({ status: "short", key, stock: estimate.stock });
        return;
      case "undeliverable":
        set({ status: "undeliverable", key });
        return;
      case "failed":
        set({ status: "failed", key, message: estimate.message });
        return;
    }
  };

  /** The basket's delivery for the purchase as read now. `force` asks the
   *  store again for the same basket and destination. */
  const update = (force: boolean) => {
    const key = deliveryKeyOf(lines);
    const read = purchase;
    if (!read || key === "[]" || read.sale.kind === "demo") {
      askedFor = null;
      generation++;
      if (state.status !== "idle") set({ status: "idle", key: null });
      return;
    }
    const where = askable(read, destination ?? standIn(read));
    // The basket's prices are in it: a read whose prices moved across a
    // postage step is priced again.
    const target = JSON.stringify([read.listingId, read.delivery, where, key, subtotalOf(read, lines)]);
    if (!force && target === askedFor && state.status !== "failed") return;
    askedFor = target;
    const current = ++generation;
    switch (read.delivery.kind) {
      // Tickets, postage not measured yet, and a download its store
      // emails: nothing to ask or quote.
      case "none":
      case "unmeasured":
      case "emailed":
        set({ status: "idle", key: null });
        return;
      case "ladder":
        set({
          status: "ready",
          key,
          estimateId: null,
          options: [
            {
              code: LISTING_DELIVERY_CODE,
              name: merchDeliveryLabel(read.delivery.country),
              feeMinor: vendorDeliveryFeeMinor(read.delivery.tiers, subtotalOf(read, lines)),
              ...(read.delivery.dutiesDue ? { dutiesDue: true as const } : {}),
              arrives: null,
              description: null,
            },
          ],
          destination: null,
          kind: null,
        });
        return;
      case "quoted": {
        if (!where) {
          set({ status: "idle", key: null });
          return;
        }
        set({ status: "loading", key });
        // A basket changing again within a moment is asked once, for the
        // last; a forced ask goes at once.
        const wait = force ? Promise.resolve() : sleep(DELIVERY_SETTLE_MS);
        wait
          .then(() => (current === generation && !disposed ? ask(key, read, where, current) : undefined))
          .catch(() => failed(key, current));
        return;
      }
    }
  };

  return {
    snapshot: () => state,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    /** Where the order goes, as last followed (null: the country the
     *  purchase was read for): a sheet following a new read keeps it. */
    destination: () => destination,
    /** The purchase as read now and where its order goes (null: the
     *  country it was read for): a change asks the store again. */
    follow(next: Purchase | null, where: Destination | null) {
      purchase = next;
      destination = where;
      update(false);
    },
    /** The basket is known, or changed. The same basket again changes
     *  nothing. */
    request(next: DeliveryLines) {
      lines = next;
      update(false);
    },
    /** Ask again for the basket whose answer failed. */
    retry() {
      if (state.status === "failed") update(true);
    },
    /** The answer no longer holds (a start found the store charging
     *  otherwise): the basket is asked anew. */
    invalidate() {
      update(true);
    },
    dispose() {
      disposed = true;
      generation++;
      listeners.clear();
    },
  };
}
export type PurchaseDeliveryController = ReturnType<typeof createPurchaseDelivery>;

/** The option a start pins: one of an estimate asked about the buyer's own
 *  destination (none for the listing's ladder, which the server prices). */
export function pinnedDelivery(
  quote: PurchaseDeliveryQuote,
  code: string | null,
  sheetId: string,
): { estimateId: string; sheetId: string; code: string } | null {
  if (quote.status !== "ready" || !quote.estimateId || quote.kind !== "buyer") return null;
  const option = quote.options.find((candidate) => candidate.code === code) ?? quote.options[0];
  return option ? { estimateId: quote.estimateId, sheetId, code: option.code } : null;
}
